import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePlan } from '../../contexts/PlanContext';
import LockedFeaturePage from '../Common/LockedFeaturePage';

/**
 * PlanGate — wraps a route element and shows LockedFeaturePage if the
 * plan feature is disabled. If the feature is hidden at platform level,
 * redirects away entirely.
 *
 * Single-feature: pass `feature`.
 * Any-of: pass `anyOf={['a','b','c']}` — the page is allowed if ANY of the
 * listed features is enabled (and not platform-hidden). If every feature is
 * platform-hidden, redirect; otherwise show the locked page for the first one.
 *
 * Usage: <PlanGate feature="quizAssessment"><QuizQuestionsPage /></PlanGate>
 *        <PlanGate feature="quizAssessment" label="Quiz & Assessments"><Component /></PlanGate>
 *        <PlanGate anyOf={['aiQuestionBank','aiLearning','aiMock']}><AICategoriesHub /></PlanGate>
 */
const PlanGate = ({ feature, anyOf, label, children }) => {
  const { hasPlanFeature, isFeatureHidden, loading } = usePlan();

  // While loading plan data, render children optimistically (fail open)
  if (loading) return children;

  // Any-of gate: allowed if at least one listed feature is enabled & visible.
  if (Array.isArray(anyOf) && anyOf.length > 0) {
    const anyVisible = anyOf.some((f) => !isFeatureHidden(f));
    if (!anyVisible) return <Navigate to="/" replace />;
    const anyEnabled = anyOf.some((f) => !isFeatureHidden(f) && hasPlanFeature(f));
    if (!anyEnabled) {
      const lockKey = anyOf.find((f) => !isFeatureHidden(f)) || anyOf[0];
      return <LockedFeaturePage featureKey={lockKey} featureLabel={label} />;
    }
    return children;
  }

  // Platform-level kill switch — redirect, don't even show locked page
  if (isFeatureHidden(feature)) {
    return <Navigate to="/" replace />;
  }

  if (!hasPlanFeature(feature)) {
    return <LockedFeaturePage featureKey={feature} featureLabel={label} />;
  }

  return children;
};

export default PlanGate;
